import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { login as apiLogin, setAuthToken, getToken, clearAuth, decodeUserFromToken } from '../services/authAPIS';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getToken());
  const normalizeUser = (u) => {
    if (!u) return u;
    const role = u.role ? u.role.toString().toLowerCase() : u.role;
    return { ...u, ...(role ? { role } : {}) };
  };
  const [user, setUser] = useState(() => normalizeUser(decodeUserFromToken(getToken())));
  const [roleMaster, setRoleMaster] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const MIN_ROLE_LOADER_MS = 500;

  useEffect(() => {
    if (token) {
      setAuthToken(token);
    }
  }, [token]);

  const isAuthenticated = useMemo(() => Boolean(token), [token]);

  const login = async ({ loginId, password }) => {
    const newToken = await apiLogin({ loginId, password });
    setToken(newToken);
    setUser(normalizeUser(decodeUserFromToken(newToken)));
    return newToken;
  };

  const logout = () => {
    clearAuth();
    setToken('');
    setUser(null);
    setRoleMaster(null);
    setRoleLoaded(false);
    try { localStorage.removeItem('roleModules'); } catch {}
  };

  // Keep in sync with storage changes or external logout events
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') {
        const t = getToken();
        if (!t) {
          setToken('');
          setUser(null);
          setRoleMaster(null);
          setRoleLoading(false);
          setRoleLoaded(false);
          try { localStorage.removeItem('roleModules'); } catch {}
        } else {
          setToken(t);
          setUser(normalizeUser(decodeUserFromToken(t)));
        }
      }
    };
    const onLogoutEvt = () => {
      setToken('');
      setUser(null);
      setRoleMaster(null);
      setRoleLoading(false);
      setRoleLoaded(false);
      try { localStorage.removeItem('roleModules'); } catch {}
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth:logout', onLogoutEvt);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:logout', onLogoutEvt);
    };
  }, []);

  // Fetch role master based on decoded token role
  useEffect(() => {
    const roleName = user?.role;
    if (!token || !roleName) {
      setRoleMaster(null);
      setRoleLoading(false);
      setRoleLoaded(false);
      try { localStorage.removeItem('roleModules'); } catch {}
      return;
    }
    const roleKey = roleName.toString().toLowerCase();
    let cancelled = false;
    const fetchRole = async () => {
      const startedAt = Date.now();
      setRoleLoading(true);
      setRoleLoaded(false);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/roleMasters/${roleKey}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const responseData = await res.json().catch(() => ({}));
        if (!cancelled) {
          const extractedData = Array.isArray(responseData?.data) ? responseData.data[0] : (responseData?.data || null);
          setRoleMaster(extractedData);
          try {
            const mods = Array.isArray(extractedData?.modules) ? extractedData.modules : [];
            localStorage.setItem('roleModules', JSON.stringify(mods));
          } catch {}
        }
      } catch (err) {
        if (!cancelled) setRoleMaster(null);
        try { localStorage.setItem('roleModules', JSON.stringify([])); } catch {}
      } finally {
        const elapsed = Date.now() - startedAt;
        const remaining = MIN_ROLE_LOADER_MS - elapsed;
        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining));
        }
        if (cancelled) return;
        setRoleLoading(false);
        setRoleLoaded(true);
      }
    };
    fetchRole();
    return () => {
      cancelled = true;
    };
  }, [token, user?.role]);

  const value = {
    token,
    user,
    isAuthenticated,
    roleMaster,
    roleLoading,
    roleLoaded,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {roleLoading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: (theme) => theme.zIndex.modal + 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
